import React, { useState } from 'react';
import { Droppable } from '@hello-pangea/dnd';
import { Plus, X, Edit2 } from 'lucide-react';
import { Card } from './Card';
import { CardModal } from './CardModal';
import { ProjectColumn, ProjectCard } from '../../types/projects';
import { projectsService } from '../../services/projects';

interface ColumnProps {
  column: ProjectColumn;
  isEditing: boolean;
  newTitle: string;
  onTitleChange: (value: string) => void;
  onTitleSave: () => void;
  onEditStart: () => void;
  onDelete: () => void;
  setColumns: React.Dispatch<React.SetStateAction<ProjectColumn[]>>;
  readOnly?: boolean;
}

export const Column: React.FC<ColumnProps> = ({
  column,
  isEditing,
  newTitle,
  onTitleChange,
  onTitleSave,
  onEditStart,
  onDelete,
  setColumns,
  readOnly = false
}) => {
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<ProjectCard | null>(null);

  const handleAddCard = () => {
    setSelectedCard(null);
    setIsCardModalOpen(true);
  };

  const handleSaveCard = async (cardData: { 
    title: string; 
    content: string; 
    label: 'warning' | 'danger' | null;
    dueDate?: string;
    tasks?: any[];
  }) => {
    const newCard = {
      id: selectedCard?.id || `card-${Date.now()}`,
      ...cardData,
      tasks: cardData.tasks || []
    };

    setColumns(prevColumns => {
      const newColumns = prevColumns.map(col => {
        if (col.id === column.id) {
          if (selectedCard) {
            // Modification d'une carte existante
            return {
              ...col,
              cards: col.cards.map(card => 
                card.id === selectedCard.id ? newCard : card
              )
            };
          } else {
            // Ajout d'une nouvelle carte
            return {
              ...col,
              cards: [...col.cards, newCard]
            };
          }
        }
        return col;
      });

      // Sauvegarder dans Firestore
      projectsService.saveAll(newColumns).catch(console.error);
      return newColumns;
    });

    setIsCardModalOpen(false);
    setSelectedCard(null);
  };

  const handleDeleteCard = (cardId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette carte ?')) {
      return;
    }

    setColumns(prevColumns =>
      prevColumns.map(col => {
        if (col.id === column.id) {
          return {
            ...col,
            cards: col.cards.filter(card => card.id !== cardId)
          };
        }
        return col;
      })
    );
  };

  return (
    <div className="flex-shrink-0 w-80 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        {isEditing ? (
          <input
            type="text"
            value={newTitle}
            onChange={e => onTitleChange(e.target.value)}
            onBlur={onTitleSave}
            onKeyPress={e => e.key === 'Enter' && onTitleSave()}
            className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary dark:bg-gray-700 dark:text-white"
            autoFocus
          />
        ) : (
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-gray-900 dark:text-white">{column.title}</h2>
            {!readOnly && (
              <button
                onClick={onEditStart}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleAddCard}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <Plus className="w-5 h-5" />
            </button>
            <button
              onClick={onDelete}
              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      <Droppable droppableId={column.id} isDropDisabled={readOnly}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="space-y-3 min-h-[50px]"
          >
            {column.cards.map((card, index) => (
              <Card
                key={card.id}
                card={card}
                index={index}
                onEdit={() => {
                  setSelectedCard(card);
                  setIsCardModalOpen(true);
                }}
                onDelete={() => handleDeleteCard(card.id)}
                readOnly={readOnly}
                setColumns={setColumns}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>

      <CardModal
        isOpen={isCardModalOpen}
        onClose={() => {
          setIsCardModalOpen(false);
          setSelectedCard(null);
        }}
        onSave={handleSaveCard}
        card={selectedCard}
      />
    </div>
  );
};